import { forumEnvelope, invalidResponse } from '../api/forumErrors';

const record = value => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const text = value => typeof value === 'string' ? value : '';
const flag = value => typeof value === 'boolean' ? value : null;
const count = value => Number.isSafeInteger(value) && value >= 0 ? value : null;
const list = value => { if (!Array.isArray(value)) throw invalidResponse(); return value; };

export function forumId(value, allowZero = false) {
  if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  return (allowZero ? /^(0|[1-9]\d*)$/ : /^[1-9]\d*$/).test(String(value)) ? String(value) : null;
}
const requiredId = value => { const id = forumId(value); if (!id) throw invalidResponse(); return id; };

// One canonical unit inside the feature: milliseconds, never mixed Unix seconds.
export function forumTimestamp(value) {
  if (Number.isSafeInteger(value) && value > 0 && value <= 8640000000000) return value * 1000;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(value)) {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function forumUrl(value) {
  // Intentional rejection of control characters in untrusted URLs.
  // eslint-disable-next-line no-control-regex
  if (typeof value !== 'string' || /[\s\u0000-\u001f\u007f\\]/.test(value)) return null;
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch (_) { return null; }
}

const capabilityNames = {
  can_read: 'canRead', can_create_topic: 'canCreateTopic', can_reply: 'canReply',
  can_edit: 'canEdit', can_delete: 'canDelete', can_vote: 'canVote', can_favorite: 'canFavorite',
  can_pin: 'canPin', can_hide: 'canHide', can_mark_pending: 'canMarkPending',
  can_moderate: 'canModerate', can_upload: 'canUpload'
};
export function mapCapabilities(raw) {
  return Object.keys(capabilityNames).reduce((result, key) => {
    result[capabilityNames[key]] = flag(record(raw)[key]);
    return result;
  }, {});
}

export function mapSection(raw) {
  if (!raw) return null;
  const section = record(raw);
  return { id: requiredId(section.id), title: text(section.title), slug: text(section.slug),
    description: text(section.description), parentId: forumId(section.parent_id),
    weight: Number.isSafeInteger(section.weight) ? section.weight : null, canCreate: flag(section.can_create), isContainer: flag(section.is_container) === true };
}
export function mapSections(raw) {
  const { data, meta } = forumEnvelope(raw);
  const sections = list(data.sections).map(value => {
    const section = mapSection(value), topicCount = count(value.topic_count);
    if (!section || topicCount === null) throw invalidResponse();
    const latest = value.latest === null ? null : { threadId: requiredId(record(value.latest).thread_id), title: text(record(value.latest).title), activityAt: forumTimestamp(record(value.latest).activity_at) };
    if (latest && (!latest.activityAt || topicCount === 0)) throw invalidResponse();
    return { ...section, topicCount, latest };
  });
  if (count(meta.total) !== sections.length || new Set(sections.map(section => section.id)).size !== sections.length) throw invalidResponse();
  return { sections };
}
export function mapAuthor(raw) {
  const author = record(raw);
  return { id: forumId(author.id, true), name: text(author.name) || 'Usuario no disponible', avatarUrl: forumUrl(author.avatar_url) };
}
export function mapPage(raw) {
  const meta = record(raw);
  const page = count(meta.page), pages = count(meta.pages), limit = count(meta.limit), total = count(meta.total);
  if (!page || !pages || !limit || limit > 50 || total === null || page > pages || pages !== Math.max(1, Math.ceil(total / limit))) throw invalidResponse();
  return { page, pages, limit, total };
}

export function mapTopic(raw) {
  const topic = record(raw);
  if (!['topic', 'stock'].includes(topic.type)) throw invalidResponse();
  return { id: requiredId(topic.id), type: topic.type, title: text(topic.title),
    ticker: text(topic.ticker) || null, section: mapSection(topic.section), author: mapAuthor(topic.author),
    createdAt: forumTimestamp(topic.created_at), activityAt: forumTimestamp(topic.activity_at),
    replyCount: count(topic.reply_count), unreadCount: count(topic.unread_count), pinned: flag(topic.pinned) };
}

export function mapActivity(raw) {
  const { data, meta } = forumEnvelope(raw);
  if (!['threads', 'comments', 'favorites', 'notifications'].includes(data.tab)) throw invalidResponse();
  const counts = {};
  for (const key of ['threads', 'comments', 'favorites', 'notifications']) {
    counts[key] = count(record(data.counts)[key]); if (counts[key] === null) throw invalidResponse();
  }
  const page = mapPage(meta);
  if (page.total !== counts[data.tab]) throw invalidResponse();
  return { tab: data.tab, counts, page,
    unread: data.tab === 'notifications' ? requiredNotificationCount(data.unread, counts.notifications) : null,
    notifications: data.tab === 'notifications' ? list(data.notifications).map(mapNotification) : [],
    topics: ['threads', 'favorites'].includes(data.tab) ? list(data.topics).map(mapTopic) : [],
    comments: data.tab !== 'comments' ? [] : list(data.comments).map(item => ({ id: requiredId(item.id), threadId: requiredId(item.thread_id),
      title: text(item.title), excerpt: text(item.excerpt), author: mapAuthor(item.author), createdAt: forumTimestamp(item.created_at) })) };
}

function requiredNotificationCount(value, total) {
  const number = count(value);
  if (number === null || number > total) throw invalidResponse();
  return number;
}

export function mapNotification(raw) {
  const item = record(raw);
  if (typeof item.read !== 'boolean' || !['topic', 'stock'].includes(item.thread_type)) throw invalidResponse();
  return { threadId: requiredId(item.thread_id), commentId: requiredId(item.comment_id),
    parentCommentId: item.parent_comment_id === null ? null : requiredId(item.parent_comment_id),
    title: text(item.title), type: item.thread_type, read: item.read, createdAt: forumTimestamp(item.created_at),
    author: mapAuthor(item.author), excerpt: text(item.excerpt) };
}

export function mapPost(raw) {
  const post = record(raw), votes = record(post.votes);
  if (!['topic', 'comment'].includes(post.type) || typeof post.html !== 'string' || !count(post.number)) throw invalidResponse();
  const quote = record(post.quote);
  return { id: requiredId(post.id), type: post.type, number: count(post.number), author: mapAuthor(post.author),
    createdAt: forumTimestamp(post.created_at), html: text(post.html),
    attachments: list(post.attachments).map(file => ({ id: requiredId(file.id), name: text(file.name),
      url: forumUrl(file.url), mime: text(file.mime) || null, width: count(file.width), height: count(file.height) })).filter(file => file.url),
    quote: post.quote ? { id: requiredId(quote.id), author: mapAuthor(quote.author), createdAt: forumTimestamp(quote.created_at), excerpt: text(quote.excerpt),
      expandedExcerpt: text(quote.expanded_excerpt) || text(quote.excerpt), excerptTruncated: flag(quote.excerpt_truncated) } : null,
    quoteUnavailable: flag(post.quote_unavailable),
    votes: { positive: count(votes.positive), negative: count(votes.negative), own: [-1, 0, 1].includes(votes.own) ? votes.own : null },
    needsResponse: flag(post.needs_response), deleted: flag(post.deleted), capabilities: mapCapabilities(post.capabilities) };
}

export function mapBootstrap(raw) {
  const { data, meta } = forumEnvelope(raw);
  if (typeof meta.access_key !== 'string' || !meta.access_key) throw invalidResponse();
  const sections = list(data.sections).map(mapSection);
  if (sections.some(section => !section)) throw invalidResponse();
  return { uid: requiredId(meta.uid), accessKey: meta.access_key,
    sections, capabilities: mapCapabilities(data.capabilities) };
  // CSRF is deliberately not part of the model or page cache; the transport owns it.
}
export function mapTopics(raw) {
  const { data, meta } = forumEnvelope(raw);
  const page = mapPage(meta), topics = list(data.topics).map(mapTopic);
  if (topics.length > page.limit || topics.length > page.total) throw invalidResponse();
  return { topics, page };
}
export function mapThread(raw) {
  const { data, meta } = forumEnvelope(raw), thread = record(data.thread);
  if (!['topic', 'stock'].includes(thread.type)) throw invalidResponse();
  const page = mapPage(meta), posts = list(data.posts).map(mapPost);
  if (page.limit !== 10 || !posts.length || posts.length > 11 || posts[0].type !== 'topic' || posts[0].id !== forumId(thread.id)) throw invalidResponse();
  const remaining = Math.min(page.limit, page.total - (page.page - 1) * page.limit);
  if (posts.length !== remaining + 1 || posts.slice(1).some(post => post.type !== 'comment') || new Set(posts.slice(1).map(post => post.id)).size !== remaining) throw invalidResponse();
  return { thread: { id: requiredId(thread.id), title: text(thread.title), type: thread.type,
    ticker: text(thread.ticker) || null, section: mapSection(thread.section), favorite: flag(thread.favorite),
    pinned: typeof thread.pinned === 'boolean' ? thread.pinned : null,
    hiddenFromRecent: typeof thread.hidden_from_recent === 'boolean' ? thread.hidden_from_recent : null,
    capabilities: mapCapabilities(thread.capabilities) }, posts, page,
    reading: { focusCommentId: forumId(meta.focus_comment_id), readAt: forumTimestamp(meta.read_at),
      lastReadAt: forumTimestamp(meta.last_read_at), firstUnreadCommentId: forumId(meta.first_unread_comment_id),
      initialPostUnread: flag(meta.initial_post_unread) } };
}
export function mapSearch(raw) {
  const { data, meta } = forumEnvelope(raw), page = mapPage(meta);
  const hits = list(data.hits).map(hit => {
    if (!['topic', 'comment'].includes(hit.type)) throw invalidResponse();
    return { id: requiredId(hit.id), threadId: requiredId(hit.thread_id), type: hit.type,
      title: text(hit.title), excerpt: text(hit.excerpt), createdAt: forumTimestamp(hit.created_at),
      section: mapSection(hit.section), author: mapAuthor(hit.author) };
  });
  if (hits.length > page.limit || hits.length > page.total) throw invalidResponse();
  return { hits, page };
}
export function mapPending(raw) {
  const { data, meta } = forumEnvelope(raw), page = mapPage(meta);
  const items = list(data.items).map(item => {
    if (!['topic', 'comment'].includes(item.type) || !['topic', 'stock'].includes(item.thread_type) || item.needs_response !== true) throw invalidResponse();
    const id = requiredId(item.id), threadId = requiredId(item.thread_id);
    if (item.type === 'topic' && id !== threadId) throw invalidResponse();
    return { id, threadId, type: item.type, threadType: item.thread_type, title: text(item.title),
      subject: text(item.subject), createdAt: forumTimestamp(item.created_at), author: mapAuthor(item.author), section: mapSection(item.section) };
  });
  if (items.length > page.limit || items.length > page.total || new Set(items.map(item => item.type + ':' + item.id)).size !== items.length) throw invalidResponse();
  return { items, page };
}
